import { petCheckMode, petCheckBonuses, type PetEquipment } from './petEquipmentService.ts';
import type { Skill } from '../content/types.ts';
import type { PetCompanion } from '../domain/pet.ts';
import type { StatId } from '../domain/stats.ts';
import { STAT_LABELS } from '../domain/stats.ts';
import type { Roller } from '../rules/dice.ts';
import type { RollMode } from '../rules/rollModes.ts';
import { resolveSkillCheck, resolveStatCheck, type CheckResult } from '../rules/checks.ts';
import { statModForScore } from '../rules/statModifiers.ts';

const SPECIAL_ABILITY_SKILLS: Partial<Record<string, string>> = {
  taunt: 'taunt',
  catcher: 'catcher',
};

/**
 * Pets do not keep trainable Skill Ranks. Any rollable Skill/ability they have
 * uses the crawler's current Floor as its Rank. Core Rulebook pp. 230-231.
 */
export function petSkillRank(floor: number): number {
  if (!Number.isInteger(floor) || floor < 1) throw new Error(`Pet Floor must be a whole number of at least 1, received ${floor}.`);
  return floor;
}

/** Explicit GM-granted Skills plus Skills conferred by printed pet abilities. */
export function petSkillIdsFor(pet: Pick<PetCompanion, 'skillIds' | 'specialAbilities'>): string[] {
  const ids = [...pet.skillIds];
  for (const ability of pet.specialAbilities) {
    const id = SPECIAL_ABILITY_SKILLS[ability];
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** Attacks and Spells have their own execution paths; Passive Skills make no Check. */
export function petSkillProblem(skill: Skill): string | null {
  if (skill.kind === 'melee-attack' || skill.kind === 'ranged-attack') return 'Pet Attacks use the companion Attack controls.';
  if (skill.kind === 'spell') return 'Pet Spells use the companion Spell controls.';
  if (skill.traits.includes('passive') || skill.commonStat === null) return `${skill.name} does not make a Skill Check.`;
  return null;
}

export function rollPetStatCheck(
  roller: Roller,
  pet: Pick<PetCompanion, 'unenhancedStats'>,
  stat: StatId,
  mode: RollMode,
  equipment?: PetEquipment,
): CheckResult {
  return resolveStatCheck(roller, {
    stat,
    statLabel: STAT_LABELS[stat].full,
    statAbbr: STAT_LABELS[stat].abbr,
    statMod: statModForScore((equipment?.enhancedStats ?? pet.unenhancedStats)[stat]),
    mode: petCheckMode(equipment, mode, { kind: 'stat', stat, skillId: null }),
    extraComponents: petCheckBonuses(equipment, { kind: 'stat', stat, skillId: null }),
  });
}

export function rollPetSkillCheck(
  roller: Roller,
  pet: Pick<PetCompanion, 'unenhancedStats'>,
  skill: Skill,
  stat: StatId,
  floor: number,
  mode: RollMode,
  equipment?: PetEquipment,
): CheckResult {
  const problem = petSkillProblem(skill);
  if (problem) throw new Error(problem);
  return resolveSkillCheck(roller, {
    skillId: skill.id,
    skillName: skill.name,
    stat,
    statAbbr: STAT_LABELS[stat].abbr,
    rank: Math.max(0, Math.min(skill.maxRank ?? 20, petSkillRank(floor) + (equipment?.modifiers.skillRankBonuses[skill.id] ?? 0))),
    statMod: statModForScore((equipment?.enhancedStats ?? pet.unenhancedStats)[stat]),
    mode: petCheckMode(equipment, mode, { kind: 'skill', skillId: skill.id, stat }),
    extraComponents: petCheckBonuses(equipment, { kind: 'skill', skillId: skill.id, stat }),
  });
}
